import {Assets, autoDetectRenderer, Texture, Text, Container, Sprite, Graphics} from 'pixi.js';
import {remove as _remove} from 'lodash/array';
import QRCode from 'qrcode';
import Stage from './Stage';
import sound from './Sound';
import utils from '../libs/utils';
import {parseCell, cellAt, cellCenter, setDifficulty, LETTERS} from './Grid';
import {MARKERS} from '../libs/screenmap';
import {tid} from '../libs/tid';
import {avatarTexture, makeImpact, makeNotice} from './Impact';
import {makeCelebration, makePodium} from './Celebration';
import {makeTitle} from './Title';
import {connectGame, resolveProfile, ZAP_COLLECTION, GAME_COLLECTION} from './Firehose';

// zap.game must reference OUR game collection — built from the shared symbol
// so a rename cannot silently desync (rkeys are PDS-minted TIDs)
const GAME_REF_RE = new RegExp(
  '^at://([^/]+)/' + GAME_COLLECTION.replace(/\./g, '\\.') + '/([2-7a-z]+)$');
import {makeLobby} from './Lobby';

const BUILD_TAG = 'b64'; // bump every build — stale-bundle detector
const BLUE_SKY_COLOR = 0x64b0ff;
const PINK_SKY_COLOR = 0xfbb4d4;
const BOTTOM_LINK_STYLE = {
  fontFamily: 'Arial',
  fontSize: '15px',
  align: 'left',
  fill: 'white'
};

/**
 * AT Protocol Duck Hunt — the screen is the single authoritative simulation.
 * Zaps arrive as games.firehose.dh.zap records on the firehose (or from the local
 * test stub); everything is judged by arrival time on this screen.
 */
const AT = {
  ROUND_SECONDS: 90,      // duck escapes (and the dog laughs) if nobody shoots it
  AMMO_PER_ROUND: 3,      // per-player shot budget — the soft rate limit
  AMMO_COOLDOWN_MS: 1500, // minimum spacing between one player's shots
  TRAIL_MS: 800,          // a zap counts if the duck was in that cell this recently…
  TRAIL_CELLS: 3,         // …AND within its last N distinct cells (fast ducks
                          // sweep half the board in 800 ms — time alone over-forgives)
  RETRIEVE_MS: 2600,      // let the dog hold the duck up before celebrating
  CELEBRATION_SECONDS: 5,
  PODIUM_SECONDS: 10,
  MAX_PLAYERS: 1,          // one hunter per session — solo duels with the duck
  LOBBY_COUNTDOWN: 5       // solo: just enough time to raise the camera
};

// Local demo roster: clicking a cell (or typing "b2") snaps as one of these.
const TEST_HANDLES = ['sri.xyz', 'atproto.nyc', 'pfrazee.com', 'danabra.mov', 'jay.bsky.team'];

// Game ids are TIDs — atproto's native record-key format: sortable, unique,
// and usable verbatim as the rkey when the game record itself gets published.
const newLobbyId = tid;

async function textureFromDataUrl(dataUrl) {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  return Texture.from(img);
}

class Game {
  constructor(opts) {
    this.spritesheet = opts.spritesheet;
    this.muted = false;
    this.paused = false;
    this.activeSounds = [];

    this.gameNumber = 0;
    this.lobbyId = '';
    this.round = 0;
    this.roundActive = false;
    this.roundEnding = false;
    this.leaderboard = new Map(); // did -> {handle, avatar, catches}
    this.ammo = new Map();        // did -> {shots, lastAt} — reset each round
    this.ammoNotices = new Set();
    this.duckTrail = [];          // [{cell, t}] — where the duck's been lately
    this.quackingSoundId = null;

    const query = new URLSearchParams(window.location.search);
    // `?anygame=1` counts shots regardless of their game id (handy while the
    // at.new link format is unverified and testers can't carry the current id).
    this.acceptAnyGame = query.get('anygame') === '1';
    // `?difficulty=easy|medium|hard` — auto-boot for unattended kiosk launches;
    // without it, the touch title screen asks (GAME A/B/C style).
    this.urlDifficulty = ['easy', 'medium', 'hard'].includes(query.get('difficulty'))
      ? query.get('difficulty') : null;
    this.difficultyName = this.urlDifficulty || 'easy';
    this.difficulty = setDifficulty(this.difficultyName);
    this.titleActive = false;
    this.titleRows = [];
    this.podiumActive = false;
    this.podiumTimer = null;
    this.players = new Map();     // did -> {handle, avatar, texture} — the locked hunters
    this.playerNotices = new Set();
    this.lobbyActive = false;
    this.lobbyCountdown = null;
    this.lobbyTimer = null;
    this.lobbyQrTexture = null;
    // `?stub=1` enables local demo input (click/keys simulate joins + shots)
    this.stubMode = query.get('stub') === '1';

    this.testPlayers = TEST_HANDLES.map((handle) => ({did: 'test:' + handle, handle, avatar: null}));
    this.testIndex = 0;
    return this;
  }

  async load() {
    this.renderer = await autoDetectRenderer({
      width: window.innerWidth,
      height: window.innerHeight,
      background: 0x000000 // letterbox bars — the sky is a rect inside the stage
    });
    await Assets.load(this.spritesheet);
    this.onLoad();
  }

  onLoad() {
    document.body.appendChild(this.renderer.canvas);

    this.stage = new Stage({
      spritesheet: this.spritesheet
    });

    console.log('[duckhunt] build', BUILD_TAG);
    // diagnostics live in markersPanel — the TOPMOST layer — so they stay
    // visible over the title/lobby/podium overlays (the HUD does not)
    const tag = new Text({
      text: BUILD_TAG,
      style: {fontFamily: 'Courier New, monospace', fontSize: 11, fill: 0xb08a4a}
    });
    tag.anchor.set(0.5, 1);
    tag.position.set(400, 598); // bottom-centre — corners belong to the markers
    this.stage.addChild(tag); // stage root, appended last = topmost; nothing clears it
    if (new URLSearchParams(window.location.search).get('debug') === '1') {
      const dbg = new Text({
        text: '',
        style: {fontFamily: 'Courier New, monospace', fontSize: 12, fill: 0xffe08a,
          stroke: {color: 0x000000, width: 3}}
      });
      dbg.anchor.set(0.5, 1);
      dbg.position.set(400, 582);
      this.stage.addChild(dbg);
      setInterval(() => {
        const players = [...this.players.entries()]
          .map(([d, p]) => `@${p.handle}:${p.gameRkey || 'NO-RKEY'}`).join(' ');
        dbg.text =
          `${BUILD_TAG} lobby=${this.lobbyId} ${this.difficultyName} round=${this.round} ` +
          `active=${this.roundActive} ending=${this.roundEnding} lobby=${this.lobbyActive} ` +
          `players[${players}]`;
      }, 500);
    }
    this.scaleToWindow();
    void this.addCornerMarkers();
    this.addMuteLink();
    this.addFullscreenLink();
    this.updateDayBoardPanel();
    this.bindEvents();
    this.connectFirehose();
    this.loadTestRoster();
    if (this.urlDifficulty) {
      this.showLobby();
    } else {
      this.showTitle();
    }
    this.animate();
  }

  /** Corner QRs the phone tracks; the payload also carries the CURRENT
   *  grid (dh:tl:4x3) — that is how the phone follows per-round changes. */
  async addCornerMarkers() {
    // rapid round changes race the async QR work — only the newest
    // invocation may publish, or phones get stale grid dimensions
    this.markerGen = (this.markerGen || 0) + 1;
    const gen = this.markerGen;
    const textures = {};
    for (const key of Object.keys(MARKERS)) {
      try {
        const dataUrl = await QRCode.toDataURL(
          `dh:${key}:${this.difficulty.cols}x${this.difficulty.rows}`, {
          margin: 1, scale: 8, errorCorrectionLevel: 'M',
          color: {dark: '#000000ff', light: '#f0d9a8ff'}
        });
        textures[key] = await textureFromDataUrl(dataUrl);
      } catch (e) { /* marker skipped; phone falls back to single-QR anchor */ }
    }
    if (gen !== this.markerGen) {
      for (const t of Object.values(textures)) {
        t.destroy(true); // superseded — don't leak the freshly baked QRs
      }
      return; // a newer grid's markers are on their way
    }
    this.stage.setCornerMarkers(textures, MARKERS);
  }

  /* ------------------------------------------------ game / round flow --- */

  /** All-day leaderboard, per screen, survives reloads (localStorage). */
  dayBoard() {
    // LOCAL calendar day — toISOString is UTC, which rolls over at 8pm in
    // New York and would wipe the board mid-event
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-` +
      `${String(d.getDate()).padStart(2, '0')}`;
    try {
      const raw = JSON.parse(localStorage.getItem('dh-dayboard-v2') || 'null');
      if (!raw || raw.date !== today || !raw.entries || typeof raw.entries !== 'object') {
        if (raw) {
          localStorage.removeItem('dh-dayboard-v2'); // yesterday's board
        }
        return {date: today, entries: {}};
      }
      return raw;
    } catch (e) {
      return {date: today, entries: {}};
    }
  }

  /** The day board keeps each hunter's BEST STREAK today — not a running
   *  total across games (which read as inflated nonsense, e.g. ×57). */
  bumpDayBoard(did, handle, streak) {
    const board = this.dayBoard();
    const entry = board.entries[did] || {handle, catches: 0};
    entry.catches = Math.max(entry.catches, streak);
    entry.handle = handle;
    board.entries[did] = entry;
    try {
      localStorage.setItem('dh-dayboard-v2', JSON.stringify(board));
    } catch (e) { /* storage full/blocked — day board is best-effort */ }
    this.updateDayBoardPanel();
  }

  /** The signed-in hunter: avatar + handle in the LEFT letterbox bar when
   *  the panel has one (Portal is 3:2); bottom-centre on a true-4:3 window. */
  async showHunterChip(profile) {
    this.clearHunterChip();
    const gen = this.chipGen; // capture AFTER the clear bumped it
    const r = 20;
    const chip = new Container();
    const texture = await avatarTexture(profile.avatar);
    if (gen !== this.chipGen) {
      return; // session changed while the avatar loaded — discard this chip
    }
    if (texture) {
      const face = new Sprite(texture);
      face.width = r * 2;
      face.height = r * 2;
      face.position.set(-r, -r);
      const mask = new Graphics().circle(0, 0, r).fill(0xffffff);
      face.mask = mask;
      chip.addChild(face, mask);
    } else {
      chip.addChild(new Graphics().circle(0, 0, r).fill(0x2a2a2f));
    }
    chip.addChild(new Graphics().circle(0, 0, r).stroke({color: 0xffffff, width: 2}));
    this.hunterChipName = new Text({
      text: `@${profile.handle}`,
      style: {fontFamily: 'Courier New, monospace', fontSize: 16, fontWeight: '700',
        fill: 0xffffff, stroke: {color: 0x000000, width: 3}}
    });
    chip.addChild(this.hunterChipName);
    this.hunterChip = chip;
    this.positionSidePanels();
    this.stage.addChild(chip); // stage root — survives overlay swaps and marker redraws
  }

  clearHunterChip() {
    this.chipGen = (this.chipGen || 0) + 1; // invalidates in-flight chip builds
    if (this.hunterChip) {
      // avatar texture is CACHED and shared — children only, never textures
      this.stage.removeChild(this.hunterChip).destroy({children: true});
      this.hunterChip = null;
      this.hunterChipName = null;
    }
  }

  /** NES-style shells for the session's hunter — spent ones go hollow, and
   *  a persistent 'out of ammo' label sits under them until the next round
   *  reloads (the phone stays silent on purpose: the NES zapper also kept
   *  clicking when empty). */
  updateShellsDisplay() {
    if (!this.shellsDisplay) {
      this.shellsDisplay = new Graphics();
      this.stage.addChild(this.shellsDisplay);
    }
    if (!this.ammoLabel) {
      this.ammoLabel = new Text({
        text: '',
        style: {fontFamily: 'Courier New, monospace', fontSize: 14, fontWeight: '700',
          align: 'center', fill: 0xffe08a, stroke: {color: 0x000000, width: 3}}
      });
      this.ammoLabel.anchor.set(0.5, 0);
      this.stage.addChild(this.ammoLabel);
    }
    const g = this.shellsDisplay.clear();
    const player = [...this.players.keys()][0];
    const spent = player ? (this.ammo.get(player) || {shots: 0}).shots : 0;
    this.ammoDry = !!player && spent >= AT.AMMO_PER_ROUND;
    if (player) {
      for (let i = 0; i < AT.AMMO_PER_ROUND; i++) {
        const x = (i - (AT.AMMO_PER_ROUND - 1) / 2) * 16;
        if (i < AT.AMMO_PER_ROUND - spent) {
          g.roundRect(x - 4, -10, 8, 20, 3).fill(0xfc6955).stroke({color: 0x7e6500, width: 1});
        } else {
          g.roundRect(x - 4, -10, 8, 20, 3).stroke({color: 0x7e6500, width: 2});
        }
      }
    }
    this.positionSidePanels();
  }

  /** Live protocol feed: recent records for this session shown as the
   *  actual AT Protocol records they are — the pitch, made visible.
   *  Newest first; both the game (join) record and every zap. */
  pushZapCard(handle, record, collection) {
    if (!this.zapFeed) {
      this.zapFeed = new Container();
      this.stage.addChild(this.zapFeed);
    }
    const card = new Container();
    // the hunter authors these fields — bound them so a hand-crafted record
    // cannot balloon the text texture; JSON.stringify escapes and renders a
    // missing game as real null, not the string "null"
    const nsid = collection || ZAP_COLLECTION;
    const body = nsid === GAME_COLLECTION
      // the hunter claiming the lobby — the session's first record
      ? `"lobby": ${JSON.stringify(String(record.lobby || '').slice(0, 20))}`
      : `"cell": ${JSON.stringify(String(record.cell || '').slice(0, 12))},\n` +
        `"game": ${JSON.stringify(record.game ? String(record.game).slice(0, 120) : null)}`;
    card.body = new Text({
      text: `@${handle} · ${nsid}\n{\n${body}\n}`,
      style: {fontFamily: 'Courier New, monospace', fontSize: 12, fill: 0xffffff,
        lineHeight: 16, wordWrap: true, breakWords: true, wordWrapWidth: 140}
    });
    card.bg = new Graphics();
    card.addChild(card.bg, card.body);
    card.bornAt = Date.now();
    this.zapFeed.addChildAt(card, 0);
    while (this.zapFeed.children.length > 4) {
      this.zapFeed.removeChildAt(4).destroy({children: true});
    }
    this.positionSidePanels();
  }

  /** Feed cards live ~6 s: arrive, linger, fade out, gone. */
  tickZapFeed() {
    if (!this.zapFeed || !this.zapFeed.children.length) {
      return;
    }
    const now = Date.now();
    let removed = false;
    for (const card of [...this.zapFeed.children]) {
      const age = now - card.bornAt;
      if (age > 6000) {
        this.zapFeed.removeChild(card).destroy({children: true});
        removed = true;
      } else {
        card.alpha = age < 5000 ? 1 : 1 - (age - 5000) / 1000;
      }
    }
    if (removed) {
      this.positionSidePanels();
    }
  }

  /** Lay out the letterbox-bar UI: avatar chip in the left bar, leaderboard
   *  in the right, both rotated to read along the bar. Recomputed on resize
   *  because bar width depends on the window's aspect ratio. */
  positionSidePanels() {
    const bar = this.stage.sideBarUnits();
    if (this.hunterChip) {
      // horizontal whenever it fits (the bar is wide when browser chrome
      // narrows the viewport); shrink, then rotate, only when it doesn't
      const name = this.hunterChipName;
      if (bar >= 44) {
        name.rotation = 0;
        name.anchor.set(0.5, 0);
        name.style.fontSize = 16;
        if (name.width > bar - 10) {
          name.style.fontSize = 12;
        }
        name.position.set(0, 30);
        if (name.width > bar - 10) {
          name.style.fontSize = 16;
          name.anchor.set(0.5);
          name.rotation = -Math.PI / 2;
          name.position.set(0, 34 + name.width / 2);
        }
        this.hunterChip.position.set(800 + Math.round(bar / 2), 48);
      } else {
        name.rotation = 0;
        name.style.fontSize = 16;
        name.anchor.set(0, 0.5);
        name.position.set(28, 0);
        this.hunterChip.position.set(Math.round(400 - this.hunterChip.width / 2), 560);
      }
    }
    if (this.shellsDisplay) {
      // the shells live directly under the avatar + handle column
      const nameRotated = !!this.hunterChipName && this.hunterChipName.rotation !== 0;
      const shellsY = bar >= 44 ? (nameRotated ? 150 : 118) : 514;
      this.shellsDisplay.position.set(
        bar >= 44 ? 800 + Math.round(bar / 2) : 400, shellsY);
      if (this.ammoLabel) {
        if (!this.ammoDry) {
          this.ammoLabel.text = '';
        } else if (bar >= 44) {
          this.ammoLabel.style.fontSize = 14;
          this.ammoLabel.text = 'out of ammo';
          if (this.ammoLabel.width > bar - 8) {
            this.ammoLabel.style.fontSize = 12;
            this.ammoLabel.text = 'out of\nammo';
          }
          this.ammoLabel.position.set(800 + Math.round(bar / 2), nameRotated ? 96 : 138);
        } else {
          this.ammoLabel.style.fontSize = 14;
          this.ammoLabel.text = 'out of ammo';
          this.ammoLabel.position.set(400, 490);
        }
      }
    }
    if (this.zapFeed) {
      // the WHOLE left bar belongs to the event feed: full records on dark
      // card backings, word-wrapped — readable or hidden, never shrunk
      this.zapFeed.visible = bar >= 90;
      if (this.zapFeed.visible) {
        const w = Math.round(bar - 12);
        this.zapFeed.position.set(Math.round(-bar / 2), 24);
        let y = 0;
        for (const card of this.zapFeed.children) {
          card.body.style.wordWrapWidth = w - 16;
          card.body.position.set(-w / 2 + 8, 8);
          card.bg.clear().roundRect(-w / 2, 0, w, card.body.height + 16, 6)
            .fill({color: 0x000000, alpha: 0.72});
          card.position.set(0, y);
          y += card.body.height + 26;
        }
      }
    }
    if (this.dayPanel) {
      const inBar = bar >= 44;
      const width = inBar ? Math.max(40, bar - 6) : 150;
      this.dayListTop = 26;
      // full-height board when nobody is hunting; below the avatar/shells
      // cluster (which only exists mid-game) otherwise
      const boardTop = inBar ? (this.hunterChip ? 170 : 40) : 72;
      this.dayViewH = inBar ? 600 - boardTop - 40 : 140;
      this.dayPanel.position.set(inBar ? 800 + Math.round(bar / 2) : 720, boardTop);
      this.dayListMask.clear()
        .rect(-width / 2, this.dayListTop, width, this.dayViewH).fill(0xffffff);
      for (const line of this.dayList.children) {
        line.scale.set(1);
        if (line.width > width - 4) {
          line.scale.set((width - 4) / line.width); // long handles shrink to fit
        }
      }
      if (this.dayCount * 18 <= this.dayViewH) {
        this.dayList.y = this.dayListTop;
      }
    }
  }

  showTitle() {
    this.roundActive = false;
    this.clearHunterChip();
    // back at the menu = the session is over; an empty roster is what makes
    // the shells column (and a lingering 'out of ammo') disappear
    this.players = new Map();
    this.ammo = new Map();
    if (this.zapFeed) {
      this.zapFeed.removeChildren(); // game over — no stale protocol cards
    }
    this.updateShellsDisplay();
    this.roundEnding = false;
    this.stage.cleanUpDucks();
    this.stage.clearImpacts();
    const title = makeTitle(); // the day board lives in the right-bar panel
    this.titleRows = title.hitRows;
    this.titleActive = true;
    this.stage.showOverlay(title.container);
  }

  selectDifficulty() {
    // one mode now — every game opens on the ladder's first rung
    this.difficultyName = 'easy';
    this.difficulty = setDifficulty('easy');
    this.stage.rebuildGrid();
    void this.addCornerMarkers();
    this.titleActive = false;
    this.stage.clearOverlay();
    this.showLobby();
  }

  /** A fresh session: new game id, empty roster, join QR up. */
  showLobby() {
    this.gameNumber += 1;
    this.lobbyId = newLobbyId();
    this.round = 0;
    this.roundActive = false;
    this.roundEnding = false;
    this.leaderboard = new Map();
    if (this.zapFeed) {
      this.zapFeed.removeChildren();
    }
    this.players = new Map();
    this.clearHunterChip();
    this.updateShellsDisplay();
    this.playerNotices = new Set();
    this.lobbyActive = true;
    this.lobbyCountdown = null;
    clearInterval(this.lobbyTimer);
    this.stage.cleanUpDucks();
    this.stage.clearImpacts();
    this.setGameStatus('');
    this.setRoundStatus('');
    void this.renderLobby(true);
  }

  /** The join link is just the game id — the grid rides the corner
   *  markers, and the lobby QR is the only join QR on screen. */
  joinLink() {
    const dir = window.location.pathname.replace(/[^/]*$/, '');
    return `${window.location.origin}${dir}play.html?lobby=${this.lobbyId}`;
  }

  async renderLobby(freshQr) {
    const lobby = this.lobbyId; // a NEW session may open during the awaits —
    // lobbyActive alone would let this stale render paint the OLD QR
    const staleQr = (freshQr || !this.lobbyQrTexture) ? this.lobbyQrTexture : null;
    if (freshQr || !this.lobbyQrTexture) {
      try {
        const dataUrl = await QRCode.toDataURL(this.joinLink(), {
          margin: 1, scale: 8, color: {dark: '#000000ff', light: '#f0d9a8ff'}
        });
        this.lobbyQrTexture = await textureFromDataUrl(dataUrl);
      } catch (e) {
        this.lobbyQrTexture = null;
      }
    }
    if (!this.lobbyActive || lobby !== this.lobbyId) {
      return;
    }
    const players = await Promise.all([...this.players.values()].map(async (p) => ({
      handle: p.handle, texture: await avatarTexture(p.avatar)
    })));
    if (!this.lobbyActive || lobby !== this.lobbyId) {
      return;
    }
    this.stage.showOverlay(makeLobby({
      qrTexture: this.lobbyQrTexture,
      players,
      countdown: this.lobbyCountdown
    }));
    if (staleQr && staleQr !== this.lobbyQrTexture) {
      staleQr.destroy(true); // its sprite died with the old overlay just now
    }
  }

  /** A join record arrived for this session — lock the hunter in. */
  async handleJoin(did, record, rkey) {
    if (record.lobby !== this.lobbyId && !this.acceptAnyGame) {
      return;
    }
    const admitted = this.players.get(did);
    if (admitted) {
      // the hunter re-published (cleared storage, retried, second tab):
      // their zaps will reference the NEWEST game record — track it, even
      // mid-game, or every subsequent zap fails the ref match
      if (rkey && admitted.gameRkey !== rkey) {
        admitted.gameRkey = rkey;
      }
      return;
    }
    if (!this.lobbyActive && !this.acceptAnyGame) {
      return; // NEW admissions happen in an OPEN lobby only — never on the menu
    }
    if (this.players.size >= AT.MAX_PLAYERS) {
      return;
    }
    // reserve the slot SYNCHRONOUSLY — two joins racing the async profile
    // fetch must not both pass the capacity check above
    const entry = {handle: did.replace('did:plc:', '').slice(0, 10), avatar: null, gameRkey: rkey || null};
    this.players.set(did, entry);
    const lobby = this.lobbyId; // the session may end during the await
    const profile = await resolveProfile(did);
    if (this.lobbyId !== lobby || this.players.get(did) !== entry) {
      return; // stale join — a different session owns the stage now
    }
    entry.handle = profile.handle;
    entry.avatar = profile.avatar;
    void this.showHunterChip(profile);
    this.updateShellsDisplay();
    if (this.lobbyActive) {
      if (this.lobbyCountdown == null) {
        this.lobbyCountdown = AT.LOBBY_COUNTDOWN;
        this.lobbyTimer = setInterval(() => {
          this.lobbyCountdown -= 1;
          if (this.lobbyCountdown <= 0) {
            clearInterval(this.lobbyTimer);
            this.beginRounds();
          } else {
            void this.renderLobby(false);
          }
        }, 1000);
      }
      void this.renderLobby(false);
    }
  }

  beginRounds() {
    this.lobbyActive = false;
    this.lobbyCountdown = null;
    this.stage.clearOverlay();
    this.setGameStatus('DUCK HUNT');
    this.stage.preLevelAnimation().then(() => {
      this.setGameStatus('');
      this.startRound();
    });
  }

  startRound() {
    this.round += 1;
    // survival ladder: rounds 1-2 easy, 3-4 medium, 5-10 hard, 11+ insane
    const rung = this.round <= 2 ? 'easy'
      : this.round <= 4 ? 'medium'
        : this.round <= 10 ? 'hard' : 'insane';
    if (rung !== this.difficultyName) {
      this.difficultyName = rung;
      this.difficulty = setDifficulty(rung);
      this.stage.rebuildGrid();
      void this.addCornerMarkers(); // marker payloads carry the new grid
    }
    this.roundActive = true;
    this.roundEnding = false;
    this.ammo = new Map();
    this.ammoNotices = new Set();
    this.updateShellsDisplay();
    this.duckTrail = [];
    this.roundStartTime = Date.now();
    this.timePaused = 0;
    this.stage.clearImpacts();
    this.stage.cleanUpDucks(); // the previous round's (dead) duck must go, or
                               // ducks[0] stays a corpse and hits never judge

    this.setRoundStatus(`round ${this.round} · ${this.difficultyName} ${this.difficulty.cols}×${this.difficulty.rows}`);
    this.quackingSoundId = sound.play('quacking');
    this.stage.addDucks(1, this.duckSpeed());
  }

  duck() {
    return this.stage.ducks[0] || null;
  }

  /** Run fn now, or once unpaused — pause must not let a timer tear down a
   *  celebration nobody saw. */
  whenResumed(fn) {
    if (!this.paused) {
      fn();
      return;
    }
    const poll = setInterval(() => {
      if (!this.paused) {
        clearInterval(poll);
        fn();
      }
    }, 200);
  }

  /** The ladder's own pace early, then a ramp; insane keeps climbing.
   *  INTEGER steps only — Duck's speed setter switches on exact values. */
  duckSpeed() {
    if (this.round <= 4) {
      return this.difficulty.speed;
    }
    if (this.round <= 10) {
      return Math.min(7, 2 + (this.round - 5)); // hard era: 2..7
    }
    return Math.min(10, 8 + Math.floor((this.round - 11) / 3)); // insane: 8..10
  }

  roundElapsedSeconds() {
    return ((Date.now() - this.roundStartTime) / 1000) - this.timePaused;
  }

  timeoutRound() {
    // nobody snapped it — the duck escapes and the dog has opinions
    this.roundActive = false;
    this.roundEnding = true;
    sound.stop(this.quackingSoundId);
    this.stage.skyColor(PINK_SKY_COLOR);
    this.stage.flyAway().then(() => {
      this.stage.skyColor(BLUE_SKY_COLOR);
      this.nextRound(false);
    });
  }

  nextRound(caught) {
    this.stage.clearImpacts();
    if (caught) {
      this.startRound(); // survival: the hunt continues only on a catch
    } else {
      this.endGame();
    }
  }

  async endGame() {
    const ranked = [...this.leaderboard.values()].sort((a, b) => b.catches - a.catches);
    sound.play(ranked.length ? 'champ' : 'loserSound');
    // a slow PDS must not stall the podium — 1.5 s, then fallback discs
    const bounded = (p) => Promise.race(
      [p, new Promise((resolve) => setTimeout(() => resolve(null), 1500))]);
    const entries = await Promise.all(ranked.slice(0, 3).map(async (entry) => ({
      handle: entry.handle,
      catches: entry.catches,
      texture: await bounded(avatarTexture(entry.avatar))
    })));
    this.stage.showOverlay(makePodium(entries));
    this.podiumActive = true;
    this.podiumTimer = setTimeout(() => {
      this.podiumActive = false;
      this.showTitle(); // every game ends back at the menu
    }, AT.PODIUM_SECONDS * 1000);
  }

  /* ---------------------------------------------------------- zapping --- */

  /**
   * zap — the single entry point for every zap, from the firehose or the
   * local stub. Judged NOW, at arrival, against where the duck's been in the
   * last TRAIL_MS.
   */
  zap({cell, did, handle, avatar, arrivedAt}) {
    if (!this.roundActive || this.roundEnding || this.paused) {
      console.log('[duckhunt] zap ignored: no active round',
        {roundActive: this.roundActive, roundEnding: this.roundEnding, paused: this.paused,
         lobbyActive: this.lobbyActive, titleActive: this.titleActive});
      // an admitted hunter firing between rounds deserves to know on-screen
      if (this.players.has(did) && !this.playerNotices.has('early-' + did)) {
        this.playerNotices.add('early-' + did);
        this.stage.addImpact(makeNotice(`@${handle} — hold fire, the duck isn't up yet!`),
          {x: 400, y: 90});
        setTimeout(() => this.playerNotices.delete('early-' + did), 4000);
      }
      return;
    }

    // session lock: only the hunters who joined this game may shoot
    if (!this.players.has(did) && !this.acceptAnyGame) {
      if (!this.playerNotices.has(did)) {
        this.playerNotices.add(did);
        this.stage.addImpact(makeNotice(`@${handle} — scan to join first`), cellCenter(cell));
      }
      return;
    }

    // soft rate limit: 3 shells per round, spaced out
    const now = arrivedAt || Date.now(); // stub/console callers have no arrival time
    const ammo = this.ammo.get(did) || {shots: 0, lastAt: 0};
    if (ammo.shots >= AT.AMMO_PER_ROUND) {
      this.showAmmoNotice(did, handle, cell);
      return;
    }
    if (now - ammo.lastAt < AT.AMMO_COOLDOWN_MS) {
      console.log('[duckhunt] zap ignored: cooldown', did);
      return;
    }
    ammo.shots += 1;
    ammo.lastAt = now;
    this.ammo.set(did, ammo);
    this.updateShellsDisplay();

    console.log('[duckhunt] ZAP ACCEPTED', cell, 'from @' + handle);
    // the gun fires
    sound.play('gunSound');
    this.stage.flashScreen.visible = true;
    setTimeout(() => {
      this.stage.flashScreen.visible = false;
    }, 60);

    // judge: newest-to-oldest through the trail — inside the time window
    // AND inside the duck's last TRAIL_CELLS distinct cells
    const target = cellCenter(cell);
    const duck = this.duck();
    let hit = false;
    if (duck && duck.alive) {
      const seen = [];
      for (let i = this.duckTrail.length - 1; i >= 0; i--) {
        const entry = this.duckTrail[i];
        if (now - entry.t > AT.TRAIL_MS) {
          break;
        }
        if (!seen.includes(entry.cell)) {
          if (seen.length >= AT.TRAIL_CELLS) {
            break;
          }
          seen.push(entry.cell);
        }
        if (entry.cell === cell) {
          hit = true;
          break;
        }
      }
    }

    const impactRound = this.round; // a slow avatar load must not splash
    avatarTexture(avatar).then((texture) => { // into the NEXT round's grid
      if (this.round === impactRound) {
        this.stage.addImpact(makeImpact({texture, handle, hit}), target);
      }
    });

    if (hit) {
      this.catchDuck({did, handle, avatar, cell});
    } else if (ammo.shots >= AT.AMMO_PER_ROUND) {
      // that was the last shell and it missed — say so NOW, not on a
      // futile fourth tap
      this.showAmmoNotice(did, handle, cell);
    }
  }

  catchDuck({did, handle, avatar, cell}) {
    this.roundActive = false;
    this.roundEnding = true;
    sound.stop(this.quackingSoundId);

    const duck = this.duck();
    duck.shot();
    duck.timeline.call(() => {
      if (!this.stage.isLocked()) {
        this.stage.dog.retrieve();
      }
    });

    const entry = this.leaderboard.get(did) || {handle, avatar, catches: 0};
    entry.catches += 1;
    entry.handle = handle;
    entry.avatar = avatar || entry.avatar;
    this.leaderboard.set(did, entry);
    this.bumpDayBoard(did, handle, entry.catches);

    setTimeout(() => this.whenResumed(async () => {
      const texture = await avatarTexture(entry.avatar);
      this.stage.showOverlay(makeCelebration({texture, handle, cell, catches: entry.catches, round: this.round}));
      sound.play('ohYeah');
      setTimeout(() => this.whenResumed(() => {
        this.stage.clearOverlay();
        this.nextRound(true);
      }), AT.CELEBRATION_SECONDS * 1000);
    }), AT.RETRIEVE_MS);
  }

  showAmmoNotice(did, handle, cell) {
    if (this.ammoNotices.has(did)) {
      return;
    }
    this.ammoNotices.add(did);
    this.stage.addImpact(makeNotice(`@${handle} is out of ammo`), cellCenter(cell));
  }

  /* ------------------------------------------------------------ input --- */

  connectFirehose() {
    this.disconnectFirehose = connectGame({
      onZap: async ({did, record, arrivedAt}) => {
        arrivedAt = arrivedAt || Date.now(); // enqueue-time arrival from the pipeline
        // The game at-uri IS the session: a zap belongs here iff it
        // references the exact dh.game record this screen admitted (did +
        // rkey match). The subscription sees EVERY dh.zap globally, so this
        // is also the filter that keeps other screens' games out.
        if (!this.acceptAnyGame) {
          const player = this.players.get(did);
          const m = String(record.game || '').match(GAME_REF_RE);
          if (!player || !player.gameRkey || !m || m[1] !== did || m[2] !== player.gameRkey) {
            console.log('[duckhunt] zap ignored: game ref not this session', record.game);
            return;
          }
        }
        // every zap for THIS session goes into the live protocol feed, even
        // ones the rules reject — the point is showing the records themselves
        void resolveProfile(did).then((p) => this.pushZapCard(p.handle, record, ZAP_COLLECTION));
        const cell = parseCell(record.cell); // 'cell' is the lexicon field — no aliases
        if (!cell) {
          console.log('[duckhunt] zap rejected: cell', record.cell, 'not on the current grid');
          return;
        }
        const profile = await resolveProfile(did);
        this.zap({cell, did, handle: profile.handle, avatar: profile.avatar, arrivedAt});
      },
      onGame: ({did, rkey, record}) => {
        console.log('[duckhunt] game record from', did, 'lobby', record.lobby,
          record.lobby === this.lobbyId ? '(this lobby)' : `(not this lobby — current ${this.lobbyId})`);
        if (record.lobby === this.lobbyId || this.acceptAnyGame) {
          // the join IS an atproto event — show it landing, like the zaps
          void resolveProfile(did).then((p) =>
            this.pushZapCard(p.handle, record, GAME_COLLECTION));
        }
        void this.handleJoin(did, record, rkey);
      }
    });
  }

  /** Local demo: fake a join record from the test roster (?stub=1 / console). */
  stubJoin(handle) {
    const player = handle
      ? {did: 'test:' + handle, handle}
      : this.testPlayers[this.testIndex++ % this.testPlayers.length];
    void this.handleJoin(player.did, {lobby: this.lobbyId});
  }

  /** Resolve real avatars for the local demo roster (best effort). */
  loadTestRoster() {
    this.testPlayers.forEach(async (player) => {
      const profile = await resolveProfile(player.handle);
      player.avatar = profile.avatar;
    });
  }

  stubZap(cell) {
    // zap as one of the JOINED hunters when a session is live
    if (this.players.size > 0) {
      const joined = [...this.players.entries()];
      const [did, p] = joined[this.testIndex++ % joined.length];
      this.zap({cell, did, handle: p.handle, avatar: p.avatar});
      return;
    }
    const player = this.testPlayers[this.testIndex % this.testPlayers.length];
    this.testIndex += 1;
    this.zap({cell, did: player.did, handle: player.handle, avatar: player.avatar});
  }

  /** Console/stub entry: duckhunt.zap('b2', 'somebody.xyz') */
  zapFromConsole(cellInput, handle) {
    const cell = parseCell(cellInput);
    if (!cell) {
      return `cells are a1 through ${LETTERS[this.difficulty.cols - 1]}${this.difficulty.rows}`;
    }
    this.zap({cell, did: 'console:' + (handle || 'tester'), handle: handle || 'tester', avatar: null});
    return `zapped ${cell}`;
  }

  bindEvents() {
    window.addEventListener('resize', this.scaleToWindow.bind(this));
    this.renderer.canvas.addEventListener('pointerdown', this.handleClick.bind(this));

    // Typing a cell ("b2") snaps as a rotating test player — the demo stub.
    this.pendingLetter = null;
    document.addEventListener('keypress', (event) => {
      event.stopImmediatePropagation();
      const key = event.key.toLowerCase();

      if (this.titleActive) {
        // legacy GAME A/B/C keys all mean START — the ladder owns difficulty
        if (['1', '2', '3', 'a', 'b', 'c', ' ', 'enter'].includes(key)) {
          this.selectDifficulty();
        }
        return;
      }

      if (key === 'p') {
        this.pause();
        return;
      }
      if (key === 'm') {
        this.mute();
        return;
      }
      // stub column letters win over single-key commands (f is a column
      // on 6+ grids, j is one on the insane grid); fullscreen keeps the
      // key whenever stub mode is off — the kiosk never runs stubs
      if (this.stubMode && key === 'j' && !this.roundActive) {
        this.stubJoin();
        return;
      }
      if (this.stubMode && /^[a-j]$/.test(key)) {
        this.pendingLetter = key;
        return;
      }
      if (key === 'f') {
        this.fullscreen();
        return;
      }
      if (this.stubMode && /^[1-6]$/.test(key) && this.pendingLetter) {
        // parseCell enforces the CURRENT grid — h5 on a 4×3 round must not
        // burn a shell on a cell the duck can never occupy
        const cell = parseCell(this.pendingLetter + key);
        if (cell) {
          this.stubZap(cell);
        }
        this.pendingLetter = null;
      }
    });

    document.addEventListener('fullscreenchange', () => {
      this.stage.hud.fullscreenLink = document.fullscreenElement ? 'unfullscreen (f)' : 'fullscreen (f)';
    });

    sound.on('play', (soundId) => {
      if (this.activeSounds.indexOf(soundId) === -1) {
        this.activeSounds.push(soundId);
      }
    });
    sound.on('stop', this.removeActiveSound.bind(this));
    sound.on('end', this.removeActiveSound.bind(this));
  }

  handleClick(event) {
    const clickPoint = {
      x: event.clientX,
      y: event.clientY
    };

    if (this.titleActive) {
      const p = this.stage.getScaledClickLocation(clickPoint);
      const row = this.titleRows.find((r) => p.x >= r.x0 && p.x <= r.x1 && p.y >= r.y0 && p.y <= r.y1);
      if (row) {
        this.selectDifficulty(row.name);
      }
      return;
    }

    if (this.podiumActive) {
      clearTimeout(this.podiumTimer);
      this.podiumActive = false;
      this.showTitle();
      return;
    }

    if (this.lobbyActive) {
      if (this.stubMode) {
        this.stubJoin(); // demo: a tap in the lobby fakes a hunter joining
      }
      return;
    }

    if (this.stage.clickedMuteLink(clickPoint)) {
      this.mute();
      return;
    }
    if (this.stage.clickedFullscreenLink(clickPoint)) {
      this.fullscreen();
      return;
    }

    // demo stub (?stub=1): clicking a cell shoots it as a joined hunter
    if (this.stubMode) {
      const cell = cellAt(this.stage.getScaledClickLocation(clickPoint));
      if (cell) {
        this.stubZap(cell);
      }
    }
  }

  /* -------------------------------------------------------------- hud --- */

  setGameStatus(text) {
    if (!Object.prototype.hasOwnProperty.call(this.stage.hud, 'gameStatus')) {
      this.stage.hud.createTextBox('gameStatus', {
        style: {fontFamily: 'Arial', fontSize: '40px', align: 'center', fill: 'white'},
        location: Stage.gameStatusBoxLocation()
      });
    }
    this.stage.hud.gameStatus = text;
  }

  setRoundStatus(text) {
    if (!Object.prototype.hasOwnProperty.call(this.stage.hud, 'waveStatus')) {
      this.stage.hud.createTextBox('waveStatus', {
        style: {fontFamily: 'Arial', fontSize: '14px', align: 'center', fill: 'white'},
        location: Stage.waveStatusBoxLocation(),
        anchor: {x: 1, y: 1}
      });
    }
    this.stage.hud.waveStatus = text;
  }

  /** Permanent all-day top-hunters board — RIGHT bar, every screen. It
   *  updates live on each catch; when the list outgrows the bar it marquees
   *  bottom-to-top so every hunter of the day gets seen. */
  updateDayBoardPanel() {
    if (!this.dayPanel) {
      this.dayPanel = new Container();
      this.dayHeader = new Text({
        text: 'top hunters',
        style: {fontFamily: 'Courier New, monospace', fontSize: 15, fontWeight: '700',
          fill: 0xffe08a, stroke: {color: 0x000000, width: 3}}
      });
      this.dayHeader.anchor.set(0.5, 0);
      this.dayList = new Container();
      this.dayListMask = new Graphics();
      this.dayList.mask = this.dayListMask;
      this.dayPanel.addChild(this.dayHeader, this.dayList, this.dayListMask);
      this.dayScroll = 0;
      this.stage.addChild(this.dayPanel);
    }
    const ranked = Object.values(this.dayBoard().entries)
      .sort((a, b) => b.catches - a.catches)
      .slice(0, 60); // marquee cap — Text objects are not free
    for (const old of this.dayList.removeChildren()) {
      old.destroy({children: true}); // rebuilt on every catch — destroy or leak
    }
    ranked.forEach((e, i) => {
      const line = new Text({
        text: `@${e.handle} ×${e.catches}`,
        style: {fontFamily: 'Courier New, monospace', fontSize: 13, fontWeight: '700',
          fill: 0xffffff, stroke: {color: 0x000000, width: 3}}
      });
      line.anchor.set(0.5, 0);
      line.position.set(0, i * 18);
      this.dayList.addChild(line);
    });
    this.dayCount = ranked.length;
    this.positionSidePanels();
  }

  /** Marquee step for an overflowing day board — called every frame. */
  tickDayBoard() {
    if (!this.dayPanel || !this.dayViewH) {
      return;
    }
    const contentH = this.dayCount * 18;
    if (contentH <= this.dayViewH) {
      this.dayList.y = this.dayListTop;
      return;
    }
    this.dayScroll += 0.5; // ≈30 px/s at 60 fps
    const cycle = contentH + this.dayViewH;
    if (this.dayScroll > cycle) {
      this.dayScroll -= cycle;
    }
    this.dayList.y = this.dayListTop + this.dayViewH - this.dayScroll;
  }

  addFullscreenLink() {
    this.stage.hud.createTextBox('fullscreenLink', {
      style: BOTTOM_LINK_STYLE,
      location: Stage.fullscreenLinkBoxLocation(),
      anchor: {x: 1, y: 1}
    });
    this.stage.hud.fullscreenLink = 'fullscreen (f)';
  }

  addMuteLink() {
    this.stage.hud.createTextBox('muteLink', {
      style: BOTTOM_LINK_STYLE,
      location: Stage.muteLinkBoxLocation(),
      anchor: {x: 1, y: 1}
    });
    this.stage.hud.muteLink = 'mute (m)';
  }

  /* -------------------------------------------------------- machinery --- */

  fullscreen() {
    this.isFullscreen = !this.isFullscreen;
    utils.toggleFullscreen();
  }

  pause() {
    this.stage.hud.pauseLink = '';
    setTimeout(() => {
      this.paused = !this.paused;
      if (this.paused) {
        this.pauseStartTime = Date.now();
        this.stage.pause();
        this.activeSounds.forEach((soundId) => {
          sound.pause(soundId);
        });
      } else {
        this.timePaused += (Date.now() - this.pauseStartTime) / 1000;
        this.stage.resume();
        this.activeSounds.forEach((soundId) => {
          sound.play(soundId);
        });
      }
    }, 40);
  }

  removeActiveSound(soundId) {
    _remove(this.activeSounds, function(item) {
      return item === soundId;
    });
  }

  mute() {
    this.stage.hud.muteLink = this.muted ? 'mute (m)' : 'unmute (m)';
    this.muted = !this.muted;
    sound.mute(this.muted);
  }

  scaleToWindow() {
    this.renderer.resize(window.innerWidth, window.innerHeight);
    this.stage.scaleToWindow();
    this.positionSidePanels();
  }

  animate() {
    if (!this.paused) {
      this.renderer.render(this.stage);
      this.tickDayBoard();
      this.tickZapFeed();

      if (this.roundActive && !this.roundEnding) {
        // sample the duck's cell for the trailing-window judge
        const duck = this.duck();
        if (duck && duck.alive) {
          const cell = cellAt(duck.position);
          if (cell) {
            const now = Date.now();
            this.duckTrail.push({cell, t: now});
            while (this.duckTrail.length && now - this.duckTrail[0].t > AT.TRAIL_MS) {
              this.duckTrail.shift();
            }
          }
        }

        if (this.roundElapsedSeconds() >= AT.ROUND_SECONDS) {
          this.timeoutRound();
        }
      }
    }

    requestAnimationFrame(this.animate.bind(this));
  }
}

export default Game;
